import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import './ArrCodingFormEditor.scss';
import {t} from 'i18next';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import {useEffect, useRef, useState} from 'react';
import ArrTextField from '../arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../arr-select-dropdown/ArrSingleSelectDropdown';
import ArrButton from '../arr-button/ArrButton';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {CodingFormItem, ISelectItem} from '../../../shared/types';
import ArrModal from '../arr-modal/ArrModal';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import ArrToastNotification from '../arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';

const operatorsList = [
  {label: 'ADD', value: 'and'},
  {label: 'OR', value: 'or'},
];

export interface IOperatorItem {
  value?: string;
  label?: string;
  disabled?: boolean;
  [key: string]: string | number | boolean | undefined;
}

const ArrLogicOperatorSwitcher: React.FC<{
  options: IOperatorItem[];
  value: string;
  testid: string;
  handleOnChange?: (value: string) => void;
}> = ({options, value, testid, handleOnChange}) => {
  const handleChipClick = () => {
    const currentIndex = options.findIndex((opt) => opt.value === value);
    const nextIndex = (currentIndex + 1) % options.length;
    handleOnChange?.(options[nextIndex].value ?? '');
  };

  return (
    <ArrButton
      size="small"
      fullWidth
      startIcon={<SwapHorizIcon />}
      onClick={handleChipClick}
      dataTestId={testid}
    >
      {value?.toUpperCase()}
    </ArrButton>
  );
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isCodingFormType = (type: string): type is CodingFormType => {
  return Object.values(CodingFormType).includes(type as CodingFormType);
};

export enum CodingFormType {
  TAG = 'TAG',
  COMMENT = 'COMMENT',
  FIELD = 'FIELD',
  STATICTEXT = 'STATICTEXT',
  RADIOGROUP = 'RADIOGROUP',
  CHECKGROUP = 'CHECKGROUP',
}

type FieldLogicArray = {
  id: number;
  object: ISelectItem;
  condition: ISelectItem;
  operator: string;
};

interface ArrCodingFormEditorProps {
  codingFormData: CodingFormItem;
  codingFormObjects?: CodingFormItem[];
  handleRemoveClick?: () => void;
  updatedItems?: CodingFormItem[];
  getUpdatedData?: (item: CodingFormItem) => void;
  dragHandleProps?: {
    role: string;
    tabIndex: number;
    'aria-disabled': boolean;
    'aria-pressed': boolean | undefined;
    'aria-roledescription': string;
    'aria-describedby': string;
    onKeyDown?: (event: React.KeyboardEvent) => void;
    onClick?: (event: React.MouseEvent) => void;
    onDragStart?: (event: React.DragEvent) => void;
  };
  ref?: (node: HTMLElement | null) => void;
  style?: {
    transform: string | undefined;
    transition: string | undefined;
  };
}

const SPECIAL_CHAR_REGEX = /[<>]/;

const ArrCodingFormEditor: React.FC<ArrCodingFormEditorProps> = ({
  dragHandleProps,
  ref,
  style,
  codingFormData,
  codingFormObjects = [],
  handleRemoveClick,
  getUpdatedData,
  updatedItems = [],
}) => {
  const {
    type,
    systemLabel,
    friendlyLabel: name, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fieldLogic,
    helpText: initialHelpText,
    errorMessage: initialErrorMessage,
  } = codingFormData;
  const typeCasted = type as CodingFormType;

  const {
    fieldLogicAction: fieldLogicActionList,
    controlType: controlTypeList,
    defaultAction: defaultActionList,
    groupDisplayType: groupDisplayTypeList,
  } = useSelector((state: RootState) => state.codingForm.metaData);
  // Initialize expand state based on isCollapsed prop, default to false (collapsed) if not provided
  const [expand, setExpand] = useState<boolean>(
    codingFormData.isCollapsed === undefined
      ? false
      : !codingFormData.isCollapsed
  );
  const [friendlyLabel, setFriendlyLabel] = useState<string>(name ?? '');
  // Initialize tagType from API response if present
  const [tagType, setTagType] = useState<ISelectItem | null>(null);
  const [defaultAction, setDefaultAction] = useState<ISelectItem | null>(() => {
    // Initialize with value from props if available
    if (codingFormData.defaultAction) {
      const matchingAction = defaultActionList.find(
        (action) => action.defaultActionCode === codingFormData.defaultAction
      );
      return (matchingAction as unknown as ISelectItem) || null;
    }
    return null;
  });
  const [fieldLogicAction, setFieldLogicAction] = useState<ISelectItem | null>(
    null
  );
  // Accept groupAssociation as an object with value and label
  // Initialize groupAssociation from API response if present
  const [groupAssociation, setGroupAssociation] = useState<ISelectItem | null>(
    () => {
      const ga = codingFormData.groupAssociation;
      return ga && typeof ga === 'object' ? ga : null;
    }
  );
  const [groupAssociationList, setGroupAssociationList] = useState<
    ISelectItem[]
  >([]);
  const [fieldLogicGroup, setFieldLogicGroup] = useState<FieldLogicArray[]>([]);

  const lastSentUpdatedDataRef = useRef<string | null>(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  // Initialize fieldLogicGroup and fieldLogicAction from controlLogicJSon if present
  useEffect(() => {
    const json = codingFormData.fieldLogicJson;
    if (!json) return;
    if (lastParsedJsonRef.current === json) return;
    if (codingFormData.fieldLogicJson && codingFormObjects.length > 0) {
      lastParsedJsonRef.current = json;
      try {
        const logicArr = JSON.parse(codingFormData.fieldLogicJson);
        if (Array.isArray(logicArr) && logicArr.length > 0) {
          // Map logicArr to FieldLogicArray[]
          const mapped = logicArr.map((logic, idx: number) => {
            // Find object and condition from codingFormObjects if possible
            let objectItem = {};
            let conditionItem = {};
            if (logic.objectname) {
              const foundObject =
                (updatedItems &&
                  updatedItems.find(
                    (obj) => obj.systemLabel === logic.objectname
                  )) ||
                codingFormObjects.find(
                  (obj) => obj.systemLabel === logic.objectname
                );
              if (foundObject) {
                objectItem = {
                  systemLabel: foundObject.systemLabel,
                  fieldLogicObjectLabel: `${foundObject.friendlyLabel || ''} (${foundObject.systemLabel})`,
                };
              } else {
                objectItem = {
                  systemLabel: logic.objectname,
                  fieldLogicObjectLabel: logic.objectname,
                };
              }
            }
            if (logic.condition) {
              conditionItem = {
                id: logic.condition,
                label:
                  logic.condition === 'Selected'
                    ? t('codingForms.editor.selected')
                    : t('codingForms.editor.notSelected'),
              };
            }
            return {
              id: idx,
              object: objectItem,
              condition: conditionItem,
              operator: logic.operator?.toLowerCase() || '',
            };
          });
          setFieldLogicGroup(mapped);

          // Set fieldLogicAction if present in logicArr[0] or codingFormData.fieldLogicAction
          const actionCode =
            logicArr[0]?.action || codingFormData.fieldLogicAction;
          if (actionCode) {
            const foundAction = fieldLogicActionList.find(
              (a) => a.controlActionCode === actionCode
            );
            setFieldLogicAction(
              (foundAction as unknown as ISelectItem) || null
            );
          }
        }
      } catch (e) {
        console.warn('Failed to parse field logic JSON:', e);
        setFieldLogicGroup([]); // Reset to empty array on invalid JSON
        setFieldLogicAction(null);
      }
    }
  }, [
    codingFormData.fieldLogicJson,
    codingFormData.fieldLogicAction,
    codingFormObjects,
    updatedItems,
    fieldLogicActionList,
  ]);
  const [errorMessage, setErrorMessage] = useState<string>(
    initialErrorMessage || ''
  );
  const [helpText, setHelpText] = useState<string>(initialHelpText || '');
  const [disableState, setDisableState] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const idRef = useRef(0);
  const tagTypeList = useRef<ISelectItem[]>([]);
  const lastParsedJsonRef = useRef<string | null>(null);

  // Synchronize tagType with dropdown options after tagTypeList is built
  useEffect(() => {
    if (tagTypeList.current.length > 0) {
      let initialTagType: ISelectItem | null = null;

      // For TAG type, use tagType (RADIO/CHECK)
      if (type === CodingFormType.TAG) {
        const tagTypeFromAPI = codingFormData.tagType;
        if (tagTypeFromAPI) {
          initialTagType =
            tagTypeList.current.find(
              (item) => String(item.value) === String(tagTypeFromAPI)
            ) || null;
        }
      }
      // For RADIOGROUP/CHECKGROUP, use displayType (DEFAULT/DROPDOWN)
      else if (
        type === CodingFormType.RADIOGROUP ||
        type === CodingFormType.CHECKGROUP
      ) {
        const displayTypeFromAPI = codingFormData.tagType;
        if (displayTypeFromAPI) {
          initialTagType =
            tagTypeList.current.find(
              (item) => String(item.value) === String(displayTypeFromAPI)
            ) || null;
        }
      }

      setTagType(initialTagType || tagTypeList.current[0] || null);
    }
  }, [
    tagTypeList.current,
    codingFormData.tagType,
    codingFormData.displayType,
    type,
  ]);

  // Synchronize groupAssociation with dropdown options after groupAssociationList is built
  useEffect(() => {
    if (groupAssociationList.length > 0 && codingFormData.groupAssociation) {
      const groupValue =
        typeof codingFormData.groupAssociation === 'object'
          ? codingFormData.groupAssociation.value
          : codingFormData.groupAssociation;
      const found =
        groupAssociationList.find((item) => item.value === groupValue) ??
        (codingFormData.tagType && groupAssociationList[1]);
      setGroupAssociation(found || null);
    }
  }, [groupAssociationList, codingFormData.groupAssociation]);

  const updateLabel = () => {
    return type == CodingFormType.CHECKGROUP ? 'Checkbox' : 'Radio button';
  };

  const getDropdownItems = () => {
    return updatedItems
      .filter(
        (item) =>
          item.systemLabel != systemLabel &&
          item.type != CodingFormType.STATICTEXT &&
          item.groupAssociation?.value != systemLabel
      )
      .map((item) => ({
        systemLabel: item.systemLabel,
        fieldLogicObjectLabel: `${item.friendlyLabel || ''} (${item.systemLabel})`,
      }));
  };

  useEffect(() => {
    if (type === CodingFormType.TAG) {
      // For tags, use control type (RADIO/CHECK)
      tagTypeList.current = controlTypeList.map((item) => ({
        value: item.controlTypeCode,
        label:
          item.controlTypeCode === 'CHECK'
            ? 'Check Item'
            : item.controlTypeLabel,
      }));
    } else if (
      type === CodingFormType.CHECKGROUP ||
      type === CodingFormType.RADIOGROUP
    ) {
      // For groups, preserve their display type
      tagTypeList.current = groupDisplayTypeList.map((item) => ({
        value: item.displayTypeCode,
        label:
          item.displayTypeLabel === 'Default'
            ? updateLabel()
            : item.displayTypeLabel,
      }));
    } else if (type === CodingFormType.COMMENT) {
      tagTypeList.current = [
        {
          value: t('codingForms.editor.textArea'),
          label: t('codingForms.editor.textArea'),
        },
      ];
    } else if (type == CodingFormType.STATICTEXT) {
      tagTypeList.current = [
        {value: CodingFormType.STATICTEXT, label: CodingFormType.STATICTEXT},
      ];
      setErrorMessage('');
      setHelpText('');
      setDefaultAction(null);
      setGroupAssociation(null);
      setFieldLogicAction(null);
      setFieldLogicGroup([]);
    } else {
      tagTypeList.current = [
        {
          value: codingFormData.displayType ?? t('codingForms.editor.textBox'),
          label: codingFormData.displayType ?? t('codingForms.editor.textBox'),
        },
      ];
    }
  }, [type, controlTypeList]);

  useEffect(() => {
    let list: ISelectItem[] = [];

    if (type === CodingFormType.TAG && tagType?.value === 'RADIO') {
      list = [
        {value: 'false', label: t('codingForms.editor.noRadioGroup')},
        ...codingFormObjects
          .filter((i) => i.type === CodingFormType.RADIOGROUP)
          .map((i) => ({
            value: i.systemLabel,
            label: `${i.friendlyLabel || ''} (${i.systemLabel})`,
          })),
      ];
    } else if (type === CodingFormType.TAG && tagType?.value === 'CHECK') {
      list = [
        {value: 'false', label: t('codingForms.editor.noCheckGroup')},
        ...codingFormObjects
          .filter((i) => i.type === CodingFormType.CHECKGROUP)
          .map((i) => ({
            value: i.systemLabel,
            label: `${i.friendlyLabel || ''} (${i.systemLabel})`,
          })),
      ];
    } else if (type === CodingFormType.TAG) {
      list = codingFormObjects
        .filter((i) =>
          [CodingFormType.RADIOGROUP, CodingFormType.CHECKGROUP].includes(
            i.type as CodingFormType
          )
        )
        .map((i) => ({
          value: i.systemLabel,
          label: `${i.friendlyLabel || ''} (${i.systemLabel})`,
        }));
    } else {
      list = codingFormObjects
        .filter((i) => i.type === CodingFormType.CHECKGROUP)
        .map((i) => ({
          value: i.systemLabel,
          label: i.fieldLogicObjectLabel || i.systemLabel,
        }));
    }

    setGroupAssociationList(list);

    // Preserve selection if still valid

    setGroupAssociation((prev) => {
      const prevValue = prev?.value;

      if (prevValue && list.some((x) => x.value === prevValue)) {
        return list.find((x) => x.value === prevValue) || prev;
      }

      // if incoming prop has value, use it (also normalized to list object)

      const incoming =
        typeof codingFormData.groupAssociation === 'object'
          ? codingFormData.groupAssociation?.value
          : codingFormData.groupAssociation;

      if (incoming && list.some((x) => x.value === incoming)) {
        return list.find((x) => x.value === incoming) || null;
      }

      // Previous value is not valid in new list - reset to null
      // This handles the RADIO → CHECK type change scenario
      return null;
    });
  }, [
    type,
    tagType?.value,
    codingFormObjects,
    codingFormData.groupAssociation,
  ]);

  useEffect(() => {
    // Disable fields if:
    // 1. Has a group association (existing behavior)
    // 2. OR is a Radio Item (tagType === 'RADIO'), even without group association
    if (
      (groupAssociation?.value && groupAssociation?.value !== 'false') ||
      (type === CodingFormType.TAG && tagType?.value === 'RADIO')
    ) {
      setFieldLogicGroup([]);
      setFieldLogicAction(null);
      setDefaultAction(null);
      setErrorMessage('');
      setDisableState(true);
    } else {
      if (type === CodingFormType.TAG && tagType?.value === 'CHECK') {
        setDisableState(false);
      }
    }
  }, [groupAssociation, tagType, type]);

  const toggleAcordion = () => {
    setExpand((prev) => !prev);
  };

  const addFieldLogic = () => {
    setFieldLogicGroup((prev) => {
      const arr = prev.slice();
      const lastIndex = arr.length - 1;
      if (lastIndex >= 0 && !arr[lastIndex].operator) {
        arr[lastIndex] = {...arr[lastIndex], operator: 'and'};
      }
      const newItems = {
        id: idRef.current++,
        object: {},
        condition: {},
        operator: '',
      };

      arr.push(newItems);
      return ensureLastOperatorBlank(arr);
    });
  };

  const removeFeildLogic = (idToRemove: number) => {
    setFieldLogicGroup((prev) => {
      const arr = prev.filter((item) => item.id !== idToRemove);
      return ensureLastOperatorBlank(arr);
    });
  };

  const ensureLastOperatorBlank = (arr: FieldLogicArray[]) => {
    if (arr.length === 0) return arr;
    const lastIndex = arr.length - 1;
    if (arr[lastIndex].operator !== '') {
      const copy = arr.slice();
      copy[lastIndex] = {...copy[lastIndex], operator: ''};
      return copy;
    }
    return arr;
  };

  const toggleItemOperator = (idToToggle: number, value: string) => {
    setFieldLogicGroup((prev) => {
      const updated = prev.map((item) => {
        if (item.id !== idToToggle) return item;
        return {...item, operator: value};
      });
      return ensureLastOperatorBlank(updated);
    });
  };

  const toggleItemCondition = (
    idToToggle: number,
    value: ISelectItem | null
  ) => {
    setFieldLogicGroup((prev) => {
      const updated = prev.map((item) => {
        if (item.id !== idToToggle) return item;
        return {...item, condition: value ?? {}};
      });
      return ensureLastOperatorBlank(updated);
    });
  };

  const toggleItemObject = (idToToggle: number, value: ISelectItem | null) => {
    setFieldLogicGroup((prev) => {
      const updated = prev.map((item) => {
        if (item.id !== idToToggle) return item;
        return {...item, object: value ?? {}};
      });
      return ensureLastOperatorBlank(updated);
    });
  };
  // Clear field logic selections when referenced objects are deleted
  useEffect(() => {
    if (fieldLogicGroup.length === 0) return;

    const availableSystemLabels = new Set(
      codingFormObjects
        .filter(
          (item) =>
            item.systemLabel !== systemLabel &&
            item.type !== CodingFormType.STATICTEXT
        )
        .map((item) => item.systemLabel)
    );

    // Check if any field logic items reference deleted objects
    const hasInvalidReferences = fieldLogicGroup.some(
      (item) =>
        item.object?.systemLabel &&
        !availableSystemLabels.has(String(item.object.systemLabel))
    );

    if (hasInvalidReferences) {
      // Remove items that reference deleted objects
      const cleaned = fieldLogicGroup
        .map((item) => {
          // If object is deleted, clear the entire row
          if (
            item.object?.systemLabel &&
            !availableSystemLabels.has(String(item.object.systemLabel))
          ) {
            return null; // Mark for removal
          }
          return item;
        })
        .filter((item): item is FieldLogicArray => item !== null); // Remove null items

      // If all items were removed, also clear the action
      if (cleaned.length === 0) {
        setFieldLogicAction(null);
      }

      // Update with cleaned array and ensure last operator is blank
      setFieldLogicGroup(ensureLastOperatorBlank(cleaned));
    }
  }, [codingFormObjects, fieldLogicGroup, systemLabel]);

  useEffect(() => {
    const updatedData = {
      ...codingFormData,
      friendlyLabel,
      errorMessage,
      helpText,
      tagType: tagType?.value?.toString() ?? '',
      defaultAction: defaultAction?.defaultActionCode?.toString() ?? '',
      fieldLogicAction: fieldLogicAction?.controlActionCode?.toString() ?? '',
      logicRuleCount: fieldLogicGroup.length.toString(),
      groupAssociation: groupAssociation
        ? {value: groupAssociation.value, label: groupAssociation.label}
        : null,
      displayType: [
        CodingFormType.RADIOGROUP,
        CodingFormType.CHECKGROUP,
      ].includes(type as CodingFormType)
        ? (tagType?.value as string) || 'DEFAULT'
        : (tagType?.value as string),
      fieldLogic: fieldLogicGroup.map((item) => ({
        objectname: item.object.systemLabel?.toString() ?? '',
        condition: item.condition.id?.toString() ?? '',
        operator: item.operator.toString().toUpperCase() ?? '',
      })),
    };

    const serialized = JSON.stringify(updatedData);
    if (lastSentUpdatedDataRef.current !== serialized) {
      lastSentUpdatedDataRef.current = serialized;
      getUpdatedData?.(updatedData);
    }
  }, [
    codingFormData,
    defaultAction,
    tagType,
    groupAssociation,
    friendlyLabel,
    defaultAction,
    fieldLogicGroup,
    fieldLogicAction,
    errorMessage,
    helpText,
    type,
    getUpdatedData,
  ]);

  return (
    <Box className="codingform-editor-main" ref={ref} style={style}>
      <Accordion
        expanded={expand}
        aria-expanded={expand}
        data-testid="codingform-editor-accordian"
      >
        <Box
          className={`codingform-editor-header ${!expand && 'header-border'}`}
        >
          <ArrIconButton
            icon={<MenuIcon />}
            {...(dragHandleProps || {})}
            onClick={(e) => {
              e.stopPropagation();
              dragHandleProps?.onClick?.(e);
            }}
            ariaLabel="dragdrop"
            shape="default"
            size="small"
            testId="arr-coding-form-editor-dragdrop-icon-button"
          />
          <AccordionSummary
            aria-controls="panel1-content"
            id="panel1-header"
            aria-label={`${type}: ${friendlyLabel} (${systemLabel})`}
          >
            <Typography
              component="span"
              className="typo-text subhead4_size title-text"
              onClick={toggleAcordion}
              data-testid="arr-coding-form-editor-toggle-accordion"
            >
              {`${type}: ${friendlyLabel} (${systemLabel})`}
            </Typography>
          </AccordionSummary>
          <ArrIconButton
            icon={<DeleteOutlineIcon />}
            onClick={() => {
              if (
                type === CodingFormType.RADIOGROUP ||
                type === CodingFormType.CHECKGROUP ||
                type === CodingFormType.STATICTEXT
              ) {
                handleRemoveClick?.();
              } else {
                setOpenDeleteModal(true);
              }
            }}
            ariaLabel="remove-form"
            shape="default"
            size="small"
            testId="remove-icon"
          />
          <ArrIconButton
            icon={expand ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={toggleAcordion}
            ariaLabel="expand-icon"
            shape="default"
            size="small"
            testId="expand-icon"
          />
        </Box>

        <AccordionDetails>
          {type == CodingFormType.STATICTEXT ? (
            <Box className="field-group">
              <Box className="group-item field-logic">
                <ArrTextField
                  id="static-text"
                  state="enabled"
                  label={t('codingForms.editor.staticText')}
                  dataTestId="coding-form-static-text"
                  value={friendlyLabel}
                  onChange={(e) => {
                    if (SPECIAL_CHAR_REGEX.test(e.target.value)) {
                      triggerToast(
                        t('codingForms.editor.specialCharError'),
                        ToastType.Error
                      );
                      setFriendlyLabel('');
                      return;
                    }
                    setFriendlyLabel(e.target.value);
                  }}
                  supportText={t('codingForms.editor.staticHelp')}
                />
              </Box>
            </Box>
          ) : (
            <>
              <Box className="field-group">
                <Box className="group-item">
                  <ArrTextField
                    id="label-text"
                    dataTestId="coding-form-label-text"
                    state="enabled"
                    label={`${type} Label`}
                    value={friendlyLabel}
                    placeholder={name || ''}
                    onChange={(e) => {
                      if (SPECIAL_CHAR_REGEX.test(e.target.value)) {
                        triggerToast(
                          t('codingForms.editor.specialCharError'),
                          ToastType.Error
                        );
                        setFriendlyLabel('');
                        return;
                      }
                      setFriendlyLabel(e.target.value);
                    }}
                  />
                </Box>
                {[
                  CodingFormType.TAG,
                  CodingFormType.RADIOGROUP,
                  CodingFormType.CHECKGROUP,
                ].includes(typeCasted) && (
                  <Box className="group-item">
                    <ArrSingleSelectDropdown
                      dropdownLabel={
                        type != CodingFormType.TAG
                          ? t('codingForms.editor.displayType')
                          : t('codingForms.editor.tagType')
                      }
                      dropdownItems={tagTypeList.current}
                      dropdownItemKey="value"
                      dropdownItemDisplay="label"
                      selectedValues={tagType}
                      onSelectionChange={setTagType}
                      dataTestId={
                        (type != CodingFormType.TAG ? 'display' : 'tag') +
                        '-type-dropdown'
                      }
                      aria-label={
                        type != CodingFormType.TAG
                          ? t('codingForms.editor.displayType')
                          : t('codingForms.editor.tagType')
                      }
                    />
                  </Box>
                )}
                <Box className="group-item">
                  <ArrSingleSelectDropdown
                    dropdownLabel={t('codingForms.editor.defaultAction')}
                    dropdownItems={
                      disableState
                        ? [
                            {
                              defaultActionCode: '0',
                              defaultActionLabel: t(
                                'codingForms.editor.defaultActionHelp'
                              ),
                            },
                          ]
                        : (defaultActionList as unknown as ISelectItem[])
                    }
                    dropdownItemKey="defaultActionCode"
                    dropdownItemDisplay="defaultActionLabel"
                    selectedValues={
                      disableState
                        ? {
                            defaultActionCode: '0',
                            defaultActionLabel: t(
                              'codingForms.editor.defaultActionHelp'
                            ),
                          }
                        : defaultAction
                    }
                    onSelectionChange={setDefaultAction}
                    disabled={disableState}
                    dataTestId="default-action-dropdown"
                  />
                </Box>
                {type == CodingFormType.TAG && (
                  <Box className="group-item">
                    <ArrSingleSelectDropdown
                      dropdownLabel={t('codingForms.editor.groupAssociation')}
                      dropdownItems={groupAssociationList}
                      dropdownItemKey="value"
                      dropdownItemDisplay="label"
                      selectedValues={groupAssociation}
                      onSelectionChange={setGroupAssociation}
                      dataTestId="group-association-dropdown"
                    />
                  </Box>
                )}
                {[
                  CodingFormType.RADIOGROUP,
                  CodingFormType.CHECKGROUP,
                ].includes(type as CodingFormType) && (
                  <Box className="group-item">
                    <ArrTextField
                      id="help-text"
                      dataTestId="coding-form-group-help-text"
                      value={helpText}
                      onChange={(e) => {
                        if (SPECIAL_CHAR_REGEX.test(e.target.value)) {
                          triggerToast(
                            t('codingForms.editor.specialCharError'),
                            ToastType.Error
                          );
                          setHelpText('');
                          return;
                        }
                        setHelpText(e.target.value);
                      }}
                      state="enabled"
                      label={t('codingForms.editor.instructionText')}
                      fullWidth
                    />
                  </Box>
                )}
              </Box>
              <Box className="field-group">
                <Box className="group-item field-logic">
                  <Box
                    className={`field-logic-container ${disableState && 'inactive-bg'}`}
                  >
                    <Typography
                      className={`typo-text field-text ${disableState && 'inactive'}`}
                      data-testid={`arr-coding-form-editor-field-logic`}>
                      {t('codingForms.editor.fieldLogic')}
                    </Typography>

                    <Box className="field-logic-group">
                      <Box className="field-logic-left">
                        {fieldLogicGroup.map((item, index) => (
                          <Box
                            key={item.id}
                            data-testid={`field-logic-item${index}`}
                          >
                            <Box className="field-logic-group item">
                              <ArrSingleSelectDropdown
                                sizeVariant="medium"
                                dropdownLabel={t(
                                  'codingForms.editor.selectObject'
                                )}
                                dropdownItems={getDropdownItems()}
                                dropdownItemKey="systemLabel"
                                dropdownItemDisplay="fieldLogicObjectLabel"
                                selectedValues={
                                  item.object && item.object.systemLabel
                                    ? item.object
                                    : null
                                }
                                onSelectionChange={(value) => {
                                  toggleItemObject(item.id, value);
                                }}
                                showNoResultsText
                                clearSelection
                                dataTestId="select-object-dropdown"
                              />
                              <ArrSingleSelectDropdown
                                sizeVariant="medium"
                                dropdownLabel={t(
                                  'codingForms.editor.selectCondition'
                                )}
                                dropdownItems={[
                                  {
                                    id: t('codingForms.editor.selected'),
                                    label: t('codingForms.editor.selected'),
                                  },
                                  {
                                    id: t('codingForms.editor.notSelectedId'),
                                    label: t('codingForms.editor.notSelected'),
                                  },
                                ]}
                                dropdownItemKey="id"
                                dropdownItemDisplay="label"
                                selectedValues={
                                  item.condition && item.condition.id
                                    ? item.condition
                                    : null
                                }
                                onSelectionChange={(value) => {
                                  toggleItemCondition(item.id, value);
                                }}
                                clearSelection
                                dataTestId="select-condition-dropdown"
                              />

                              <ArrIconButton
                                icon={<DeleteOutlineIcon />}
                                onClick={() => removeFeildLogic(item.id)}
                                ariaLabel="remove-field-logic"
                                shape="none"
                                size="small"
                                testId={`remove-field-logic${index}`}
                              />
                            </Box>

                            {item.operator != '' && (
                              <Box className="operator-button">
                                <ArrLogicOperatorSwitcher
                                  options={operatorsList}
                                  value={item.operator}
                                  testid={`switch-btn-${index}`}
                                  handleOnChange={(value) => {
                                    toggleItemOperator(item.id, value);
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                      {fieldLogicGroup.length > 0 && (
                        <Box className="with-border">
                          <ArrSingleSelectDropdown
                            sizeVariant="medium"
                            dropdownLabel={t('codingForms.editor.selectAction')}
                            dropdownItems={
                              fieldLogicActionList as unknown as ISelectItem[]
                            }
                            dropdownItemKey="controlActionCode"
                            dropdownItemDisplay="controlActionLabel"
                            selectedValues={fieldLogicAction}
                            onSelectionChange={setFieldLogicAction}
                            clearSelection
                            dataTestId="select-action-dropdown"
                          />
                        </Box>
                      )}
                    </Box>

                    <Box>
                      {disableState ? (
                        <Typography
                          className={`typo-text field-text ${disableState && 'inactive'}`}
                          data-testid={`arr-coding-form-editor-field-logic-disable`}>
                          {t('codingForms.editor.fieldLogicDisable')}
                        </Typography>
                      ) : (
                        <ArrButton
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={addFieldLogic}
                          dataTestId="add-logic-button"
                        >
                          {t('codingForms.editor.addLogic')}
                        </ArrButton>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box className="field-group">
                <Box className="group-item">
                  <ArrTextField
                    id="error-text"
                    dataTestId="coding-form-error-text"
                    value={errorMessage}
                    onChange={(e) => {
                      if (SPECIAL_CHAR_REGEX.test(e.target.value)) {
                        triggerToast(
                          t('codingForms.editor.specialCharError'),
                          ToastType.Error
                        );
                        setErrorMessage('');
                        return;
                      }
                      setErrorMessage(e.target.value);
                    }}
                    state={disableState ? 'inactive' : 'enabled'}
                    label={
                      disableState
                        ? t('codingForms.editor.errorMessageDisable')
                        : t('codingForms.editor.errorMessage')
                    }
                    multiline
                    maxRows={4}
                    fullWidth
                    supportText={t('codingForms.editor.errorMessageHelp')}
                    disabled
                  />
                </Box>
                <Box className="group-item">
                  {![
                    CodingFormType.RADIOGROUP,
                    CodingFormType.CHECKGROUP,
                  ].includes(type as CodingFormType) && (
                    <ArrTextField
                      id="help-text"
                      dataTestId="coding-form-help-text"
                      value={helpText}
                      onChange={(e) => {
                        if (SPECIAL_CHAR_REGEX.test(e.target.value)) {
                          triggerToast(
                            t('codingForms.editor.specialCharError'),
                            ToastType.Error
                          );
                          setHelpText('');
                          return;
                        }
                        setHelpText(e.target.value);
                      }}
                      state="enabled"
                      label={t('codingForms.editor.helpText')}
                      multiline
                      maxRows={4}
                      fullWidth
                      supportText={t('codingForms.editor.helpTextHelp')}
                    />
                  )}
                </Box>
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>
      {
        <ArrModal
          showModal={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          title={t('common.delete')}
          description={t('common.areYouSure')}
          dataTestId="codingform-editor-modal"
          buttons={[
            {
              id: '1',
              label: t('common.no'),
              onClick: () => {
                setOpenDeleteModal(false);
              },
              variant: 'secondary',
              dataTestId: 'delete-modal-cancel-btn',
            },
            {
              id: '2',
              label: t('common.yes'),
              onClick: () => {
                handleRemoveClick?.();
                setOpenDeleteModal(false);
              },
              variant: 'primary',
              dataTestId: 'delete-modal-confirm-btn',
            },
          ]}
        />
      }
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={[toastStatus]}
          onClose={() => setShowToast(false)}
        />
      )}
    </Box>
  );
};

export default ArrCodingFormEditor;
